import PropTypes from 'prop-types';
import cn from 'classnames';
import { Button as MuiButton } from '@mui/material';
import PageListHeader from 'shared/_components/page/page-list-header';
import { Page } from 'shared/_components/page/styles';
import { ListFilter } from '../ListFilter';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import styles from './ListContainer.module.css';
import Button from '../Button/Button';
import ListItem from './ListItem';
import ListPlaceholder from './placeholders/ListPlaceholder';

const Actions = ({ actions }) => {
	const visibleActions = actions.filter((act) => act.visible !== false);
	if (visibleActions.length) {
		return (
			<div className={styles.actionContainer}>
				{visibleActions.map(
					({ component, label, ...actionProps }, key) =>
						component || (
							<Button key={key} {...actionProps}>
								{label}
							</Button>
						)
				)}
			</div>
		);
	}
	return '';
};
Actions.propTypes = {
	actions: PropTypes.array,
};

/** Header actions of a page list: the same MUI buttons as the other page lists. */
const PageActions = ({ actions }) => {
	const visibleActions = actions.filter((act) => act.visible !== false);
	if (!visibleActions.length) {
		return null;
	}
	return visibleActions.map(
		({ component, label, color, onClick }, key) =>
			component || (
				<MuiButton key={key} color={color === 'gray' ? 'secondary' : undefined} onClick={onClick}>
					{label}
				</MuiButton>
			)
	);
};
PageActions.propTypes = {
	actions: PropTypes.array,
};

const ListContainer = ({
	title,
	items,
	actions = [],
	type,
	titleLevel = 1,
	titleClassName = undefined,
	page = false,
	emptyState = /** @type {any} */ (undefined),
	...rest
}) => {
	if (page) {
		return (
			<Page>
				<PageListHeader count={items.length} actionsRight={<PageActions actions={actions} />}>
					{title}
				</PageListHeader>
				{rest.ready === false ? (
					<LoadingPlaceholder />
				) : (
					<>
						{items.length === 0 && (emptyState || <ListPlaceholder type={type} />)}
						{items.map((item) => (
							<ListItem item={item} type={type} key={item._id} {...rest} />
						))}
					</>
				)}
			</Page>
		);
	}

	const HeadTag = `h${titleLevel}`;
	const showTitle = (actions && actions.length) || title;
	return (
		<>
			<div className={styles.header}>
				{showTitle && (
					<HeadTag key="header" className={cn(styles.title, titleClassName)}>
						{title}
						{title && <span className={styles.count}>({items.length})</span>}
						<Actions actions={actions} />
					</HeadTag>
				)}
			</div>
			{rest.filterProps && (
				<div className={styles.filter}>
					<ListFilter {...rest.filterProps} />
				</div>
			)}
			{rest.ready === false ? (
				<LoadingPlaceholder />
			) : (
				<>
					{items.length === 0 && <ListPlaceholder type={type} />}
					{items.map((item) => (
						<ListItem item={item} type={type} key={item._id} {...rest} />
					))}
				</>
			)}
		</>
	);
};

ListContainer.propTypes = {
	title: PropTypes.node.isRequired,
	titleLevel: PropTypes.number,
	titleClassName: PropTypes.string,
	items: PropTypes.array.isRequired,
	actions: PropTypes.array,
	type: PropTypes.string.isRequired,
	ready: PropTypes.bool,
	/** Page-level list: shared page header, width limit and MUI header buttons. */
	page: PropTypes.bool,
	/** Shown instead of the default placeholder when the list is empty. */
	emptyState: PropTypes.node,
};

export default ListContainer;
